/**
 * TrainingDetailView - Read-only training record detail view with audit trail
 *
 * MCA Compliance: ISM Code Element 9, WBC3 SMS requirements
 * Shows full training record details with audit history for inspections
 */

import React, { useState, useMemo } from 'react';
import {
  GraduationCap,
  Calendar,
  Clock,
  Users,
  User,
  Award,
  FileText,
  History,
  Edit3,
  CheckCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useSwipeBack, SwipeIndicator } from '../../../../hooks/useSwipeBack';
// FT-357: shell-level back handler — bottom bar owns Back. Header BackButton removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';
import { Badge } from '../../../catalyst/badge';
import { TRAINING_CATEGORIES, COMPETENCY_LEVELS } from '../../../../config/training';
import EditReasonModal from '../../../common/EditReasonModal';
import { getCreatedByName } from '../../shared/AttributionBadge';

const TrainingDetailView = ({
  training,
  onBack,
  onEdit,
  crew = []
}) => {
  // FT-357: register onBack with the shell-level MobileBottomBar.
  useSetBackHandler(onBack);

  const { containerRef, swipeProgress } = useSwipeBack(onBack);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editReason, setEditReason] = useState('');
  const [auditExpanded, setAuditExpanded] = useState(true);

  // Get category and topic labels
  const categoryLabel = useMemo(() => {
    if (!training?.category) return 'Unknown Category';
    const cat = TRAINING_CATEGORIES[training.category];
    return cat?.label || training.category;
  }, [training?.category]);

  const topicLabel = useMemo(() => {
    if (!training?.topic) return 'Unknown Topic';
    // If topic matches a known topic ID, get its name
    if (training.category && TRAINING_CATEGORIES[training.category]) {
      const topic = TRAINING_CATEGORIES[training.category].topics?.find(t => t.id === training.topic);
      if (topic) return topic.name;
    }
    return training.topic;
  }, [training?.category, training?.topic]);

  // Get competency level label
  const competencyLabel = useMemo(() => {
    const level = COMPETENCY_LEVELS.find(l => l.id === training?.competencyLevel);
    return level?.name || 'Not assessed';
  }, [training?.competencyLevel]);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Format timestamp for audit
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle edit button click
  const handleEditClick = () => {
    setShowEditModal(true);
  };

  // Handle edit confirmation with reason
  const handleEditConfirm = (reason) => {
    setEditReason(reason);
    setShowEditModal(false);
    // Pass the reason to the edit handler
    if (onEdit) {
      onEdit(training, reason);
    }
  };

  if (!training) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Training record not found</p>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-gray-50"
    >
      <SwipeIndicator progress={swipeProgress} />

      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* FT-357: header BackButton removed — bottom bar owns Back. */}
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Training Record</h1>
                <p className="text-sm text-gray-500">View details and audit trail</p>
              </div>
            </div>
            {onEdit && (
              <button
                onClick={handleEditClick}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
              >
                <Edit3 className="h-4 w-4" />
                Edit
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto p-6 space-y-6">

        {/* Section 1: Training Information */}
        <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
          <div className="bg-slate-700 px-5 py-3">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <GraduationCap className="h-4 w-4 text-white" />
              Training Information
            </h2>
          </div>
          <div className="p-6">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Topic</dt>
                <dd className="mt-1 text-sm text-gray-900 font-medium">{topicLabel}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Category</dt>
                <dd className="mt-1 text-sm text-gray-900">{categoryLabel}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Date</dt>
                <dd className="mt-1 text-sm text-gray-900 flex items-center gap-1">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  {formatDate(training.date)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Duration</dt>
                <dd className="mt-1 text-sm text-gray-900 flex items-center gap-1">
                  <Clock className="h-4 w-4 text-gray-400" />
                  {training.duration ? `${training.duration} minutes` : 'Not recorded'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Trainer</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {training.trainer || 'Not specified'}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Competency Level</dt>
                <dd className="mt-1">
                  <Badge color="cyan">{competencyLabel}</Badge>
                </dd>
              </div>
            </dl>
          </div>
        </section>

        {/* Section 2: Trainees */}
        <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
          <div className="bg-slate-700 px-5 py-3">
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <Users className="h-4 w-4 text-white" />
              Trainees ({training.trainees?.length || 0})
            </h2>
          </div>
          <div className="p-6">
            {training.trainees && training.trainees.length > 0 ? (
              <ul className="divide-y divide-gray-100">
                {training.trainees.map((trainee, index) => (
                  <li key={trainee.id || index} className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-cyan-100 flex items-center justify-center">
                        <User className="h-4 w-4 text-cyan-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{trainee.name}</p>
                        {trainee.role && (
                          <p className="text-xs text-gray-500">{trainee.role}</p>
                        )}
                      </div>
                    </div>
                    <Badge color="green">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Completed
                    </Badge>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">No trainees recorded</p>
            )}
          </div>
        </section>

        {/* Section 3: Description & Notes */}
        {(training.description || training.notes) && (
          <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white flex items-center gap-2">
                <FileText className="h-4 w-4 text-white" />
                Description & Notes
              </h2>
            </div>
            <div className="p-6 space-y-4">
              {training.description && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">Training Description</dt>
                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{training.description}</dd>
                </div>
              )}
              {training.notes && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">Additional Notes</dt>
                  <dd className="text-sm text-gray-900 whitespace-pre-wrap">{training.notes}</dd>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Section 4: Certificate Information */}
        {training.certificateIssued && (
          <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            <div className="bg-slate-700 px-5 py-3">
              <h2 className="text-base font-semibold text-white flex items-center gap-2">
                <Award className="h-4 w-4 text-white" />
                Certificate Issued
              </h2>
            </div>
            <div className="p-6">
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Certificate Type</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {training.certificateName || training.stcwCertType || 'Certificate'}
                  </dd>
                </div>
                {training.certificateNumber && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Certificate Number</dt>
                    <dd className="mt-1 text-sm text-gray-900 font-mono">{training.certificateNumber}</dd>
                  </div>
                )}
                {training.certificateExpiry && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Expiry Date</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formatDate(training.certificateExpiry)}</dd>
                  </div>
                )}
              </dl>
            </div>
          </section>
        )}

        {/* Section 5: Audit Trail - Compact scannable format */}
        <section className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
          <button
            onClick={() => setAuditExpanded(!auditExpanded)}
            className="w-full bg-slate-700 px-5 py-3 flex items-center justify-between hover:bg-slate-500 transition-colors"
          >
            <h2 className="text-base font-semibold text-white flex items-center gap-2">
              <History className="h-4 w-4 text-white" />
              Audit Trail
            </h2>
            {auditExpanded ? (
              <ChevronUp className="h-5 w-5 text-white" />
            ) : (
              <ChevronDown className="h-5 w-5 text-white" />
            )}
          </button>
          {auditExpanded && (
            <div className="px-6 py-4 space-y-3 text-sm max-h-96 overflow-y-auto">
              {training.auditLog && training.auditLog.length > 0 ? (
                training.auditLog.map((entry, index) => (
                  <div key={entry.id || index} className="text-gray-700 pb-3">
                    <div className="font-medium">
                      • {entry.action === 'created' ? 'Created' : 'Edited'} by {entry.user} - {formatTimestamp(entry.timestamp)}
                    </div>
                    <div className="ml-4 text-gray-600 space-y-1">
                      {/* Summary */}
                      {entry.summary && entry.action !== 'created' && (
                        <div>{entry.summary}</div>
                      )}

                      {/* Field-level changes (if any) */}
                      {entry.changes && entry.changes.length > 0 && (
                        <div className="ml-2 text-sm space-y-0.5">
                          {entry.changes.map((change, changeIndex) => (
                            <div key={changeIndex} className="text-gray-500">
                              • {change.field}: "{String(change.displayOld || change.oldValue || 'empty')}" → "{String(change.displayNew || change.newValue)}"
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Reason */}
                      {entry.reason && entry.reason !== 'Initial record creation' && (
                        <div>Reason: {entry.reason}</div>
                      )}

                      {/* Vessel */}
                      {entry.vessel && (
                        <div>Vessel: {entry.vessel}</div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-gray-700">
                  <div className="font-medium">
                    • Created by {getCreatedByName(training.createdBy) || 'Unknown user'} - {training.createdAt ? formatTimestamp(training.createdAt) : formatDate(training.date)}
                  </div>
                  <div className="ml-4 text-gray-600">
                    <div>Initial record creation</div>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Record metadata footer */}
        <div className="text-center text-xs text-gray-400 py-4">
          Record ID: {training.id}
          {training.updatedAt && (
            <span className="ml-2">• Last updated: {formatTimestamp(training.updatedAt)}</span>
          )}
        </div>
      </div>

      {/* Edit Reason Modal */}
      <EditReasonModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onConfirm={handleEditConfirm}
        item={training}
        recordType="training record"
      />
    </div>
  );
};

export default TrainingDetailView;
